import { FacilityAssessment, PMSchedule, SystemKey } from '../../models/types';

/* PM rule templating, ported from durham-om's pm_tasks.json pattern:
   regex rules match facility-assessment assets (by their description — the equipment type)
   to task-library asset types, so an assessment auto-seeds a PM program. */

export interface PMRule {
  assetType: string;      // must match an assetType used in the PM task library
  pattern: RegExp;
  systems?: SystemKey[];  // restrict to these facility systems (undefined = any) —
                          // prevents e.g. "Chiller Plant Controller" (controls) from
                          // receiving chiller tube-cleaning tasks
}

export const PM_RULES: PMRule[] = [
  // Mechanical
  { assetType: 'Air Handling Unit', systems: ['mechanical'], pattern: /air handling|ahu|make-?up air|\bmau\b|doas|dedicated outdoor|unit ventilator|fan coil/i },
  { assetType: 'Split System / RTU', systems: ['mechanical'], pattern: /rooftop|rtu|split system|ptac|package terminal|vrf|heat pump|air conditioner/i },
  { assetType: 'Boiler', systems: ['mechanical'], pattern: /boiler/i },
  { assetType: 'Chiller', systems: ['mechanical'], pattern: /chiller/i },
  { assetType: 'Cooling Tower', systems: ['mechanical'], pattern: /cooling tower/i },
  { assetType: 'Condenser', systems: ['mechanical'], pattern: /condenser - |condensing unit/i },
  { assetType: 'VAV Box', systems: ['mechanical'], pattern: /vav|variable air volume|air terminal|constant volume box/i },
  { assetType: 'Exhaust Fan', systems: ['mechanical'], pattern: /exhaust fan|supply fan|return fan|relief fan/i },
  { assetType: 'Kitchen Hood', systems: ['mechanical'], pattern: /kitchen (exhaust )?hood/i },
  // "heat pump" must NOT match — enumerate real pump classifications instead
  { assetType: 'Pump', systems: ['mechanical', 'plumbing'], pattern: /water pump|circulating pump|booster pump|^pump\b/i },
  { assetType: 'Heat Exchanger', systems: ['mechanical', 'plumbing'], pattern: /heat exchanger/i },
  { assetType: 'Unit Heater / Terminal Heat', systems: ['mechanical'], pattern: /unit heater|radiant heater|radiant panel|baseboard|cabinet unit heater/i },
  { assetType: 'Humidifier', systems: ['mechanical'], pattern: /humidifier - /i },
  { assetType: 'Energy Recovery Unit', systems: ['mechanical'], pattern: /energy recovery|\berv\b|\beru\b/i },
  { assetType: 'Walk-In Refrigeration', systems: ['mechanical'], pattern: /refrigeration - |walk-in/i },
  { assetType: 'Fire / Smoke Damper', systems: ['mechanical', 'controls'], pattern: /damper - fire|fire \/ smoke|smoke damper/i },
  // Controls
  { assetType: 'Thermostat / Controls', systems: ['controls'], pattern: /thermostat|controller|actuator|control valve/i },
  { assetType: 'BAS / Controls System', systems: ['controls'], pattern: /\bbas\b|building automation|energy management|\bems\b|\bddc\b/i },
  // Electrical
  { assetType: 'Electrical Panel', systems: ['electrical'], pattern: /power panel|electrical panel|branch circuit panel|distribution panel|sub-panel|switchgear|switchboard|motor control center|\bmcc\b|disconnect|circuit breaker/i },
  { assetType: 'Generator', systems: ['electrical'], pattern: /generator/i },
  { assetType: 'UPS', systems: ['electrical'], pattern: /\bups\b|uninterruptible|battery bank/i },
  { assetType: 'Transformer', systems: ['electrical'], pattern: /transformer/i },
  { assetType: 'Automatic Transfer Switch', systems: ['electrical'], pattern: /automatic transfer|\bats\b/i },
  { assetType: 'VFD', systems: ['electrical'], pattern: /variable frequency|\bvfd\b|soft starter/i },
  { assetType: 'Emergency Lighting', systems: ['electrical'], pattern: /emergency light|exit \/ emergency/i },
  { assetType: 'Exit Sign', systems: ['electrical'], pattern: /exit sign/i },
  { assetType: 'Exterior Lighting', systems: ['electrical'], pattern: /exterior lighting|parking lot lighting|lighting pole/i },
  { assetType: 'Lighting', systems: ['electrical'], pattern: /lighting fixture/i },
  { assetType: 'GFCI Outlet', systems: ['electrical'], pattern: /gfci/i },
  // Fire & life safety
  { assetType: 'Fire Alarm', systems: ['controls'], pattern: /fire alarm|facp|smoke detector|heat detector|pull station|horn\/strobe|annunciator/i },
  { assetType: 'Fire Extinguisher', pattern: /extinguisher/i },
  { assetType: 'Sprinkler System', systems: ['plumbing'], pattern: /sprinkler|standpipe/i },
  // Plumbing
  { assetType: 'Water Heater', systems: ['plumbing'], pattern: /water heater|hot water storage/i },
  { assetType: 'Backflow Preventer', systems: ['plumbing'], pattern: /backflow/i },
  { assetType: 'PRV', systems: ['plumbing'], pattern: /pressure reducing|\bprv\b/i },
  { assetType: 'Water Treatment System', systems: ['mechanical', 'plumbing'], pattern: /water treatment|water softener|filtration|reverse osmosis|chemical feed/i },
  { assetType: 'Floor Drain', systems: ['plumbing'], pattern: /floor drain|area drain|roof drain/i },
  { assetType: 'Hose Bib', systems: ['plumbing'], pattern: /hose bib|wall hydrant/i },
  { assetType: 'Sump / Sewage Pump', systems: ['plumbing'], pattern: /sump|sewage/i },
  { assetType: 'Grease Interceptor', systems: ['plumbing'], pattern: /grease interceptor|grease trap/i },
  { assetType: 'Plumbing Fixtures', systems: ['plumbing'], pattern: /water closet|urinal|lavatory|kitchen sink|mop sink|faucet|flush valve|drinking fountain|^shower$/i },
  { assetType: 'Emergency Eye Wash / Shower', systems: ['plumbing'], pattern: /eye wash|emergency shower/i },
];

export const matchAssetTypes = (text: string, system: SystemKey): string[] => {
  const matched = PM_RULES
    .filter(r => !r.systems || r.systems.includes(system))
    .filter(r => r.pattern.test(text))
    .map(r => r.assetType);
  return Array.from(new Set(matched));
};

/* Structurally compatible with both OrgPMTask and PMTaskTemplate. */
export interface SeedTaskSource {
  assetType: string;
  taskName: string;
  trade: string;
  frequencyValue: number;
  frequencyUnit: PMSchedule['frequencyUnit'];
  estimatedHours: number;
  complianceRequired: boolean;
  complianceStandard?: string;
}

export interface SeedRow {
  key: string;
  assetDescription: string;
  assetLocation: string;
  rating?: string;
  taskName: string;
  trade: string;
  frequencyValue: number;
  frequencyUnit: PMSchedule['frequencyUnit'];
  estimatedHours: number;
  complianceRequired: boolean;
  complianceStandard?: string;
  exists: boolean;
}

const SYSTEMS: SystemKey[] = ['controls', 'electrical', 'mechanical', 'plumbing'];

export function buildSeedRows(
  assessment: FacilityAssessment,
  tasks: SeedTaskSource[],
  existing: PMSchedule[],
): { rows: SeedRow[]; unmatched: string[] } {
  const rows: SeedRow[] = [];
  const unmatched: string[] = [];
  const existingKeys = new Set(existing.map(s => `${s.assetDescription}|${s.taskName}`.toLowerCase()));
  for (const system of SYSTEMS) {
    for (const a of assessment[system].assets) {
      const label = a.description;
      if (!label) continue;  // skip blank rows
      const desc = a.assetId ? `${a.assetId} — ${label}` : label;
      const types = matchAssetTypes(a.description, system);
      const matchedTasks = tasks.filter(t => types.includes(t.assetType));
      if (!matchedTasks.length) { unmatched.push(desc); continue; }
      for (const t of matchedTasks) {
        rows.push({
          key: `${a.id}|${t.assetType}|${t.taskName}`,
          assetDescription: desc,
          assetLocation: a.location,
          rating: a.rating,
          taskName: t.taskName,
          trade: t.trade,
          frequencyValue: t.frequencyValue,
          frequencyUnit: t.frequencyUnit,
          estimatedHours: t.estimatedHours,
          complianceRequired: t.complianceRequired,
          complianceStandard: t.complianceStandard,
          exists: existingKeys.has(`${desc}|${t.taskName}`.toLowerCase()),
        });
      }
    }
  }
  return { rows, unmatched };
}
